import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import React, { ForwardedRef, forwardRef, KeyboardEventHandler, ReactElement, useCallback, useMemo, useRef, useState } from 'react';

import { useOutsideClick } from '@pflegia/ui/hooks/use-outside-click';
import { ReactComponent as Chevron } from '@pflegia/ui/icons/chevron.svg';
import { ReactComponent as Times } from '@pflegia/ui/icons/times.svg';
import { isSubstring } from '@pflegia/util/functions/string/is-substring';
import { mergeTailwindClasses } from '@pflegia/util/functions/style/merge-tailwind-classes';

import { InputState } from '../Input.component';

export interface Option {
  value?: string;
  text: string;
}

export enum DropdownVariant {
  Rounded = 'rounded',
  RoundedSmallerWithBorder = 'roundedSmallerWithBorder',
  Default = 'default',
  Primary = 'primary',
  Secondary = 'secondary',
}

const getIsRoundedBorder = (variant?: DropdownVariant) =>
  variant && [DropdownVariant.Rounded, DropdownVariant.RoundedSmallerWithBorder, DropdownVariant.Primary].includes(variant);

export interface DropdownProps<O = Option> {
  children?: React.ReactNode;
  disabled?: boolean;
  id?: string;
  label?: string;
  onBlur?: () => void;
  onChange: (option: O) => void;
  options?: O[];
  selectedOptions?: O[];
  placeholder?: string;
  size?: 'base' | 'sm';
  state?: InputState;
  value?: O | null;
  variant?: DropdownVariant;
  name?: string;
  classname?: string;
  dialogClassName?: string;
  autocomplete?: boolean;
  onSearch?: (value: string) => void;
  onTouched?: () => void;
  staticTrigger?: boolean;
}

const DropdownComponent = <O extends Option>(
  {
    children,
    disabled,
    id,
    label,
    onBlur,
    onChange,
    options,
    selectedOptions,
    placeholder,
    size = 'base',
    state,
    value,
    variant,
    name,
    classname,
    dialogClassName,
    autocomplete,
    onSearch,
    onTouched,
    staticTrigger,
    ...inputProps
  }: DropdownProps<O>,
  ref: ForwardedRef<HTMLInputElement>,
): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const isPreviouslyAdded = useCallback(
    (item?: string | O) =>
      Boolean(
        item &&
          selectedOptions?.some(selectedValue =>
            typeof item === 'string' ? isSubstring(selectedValue.text, item) : item.value === selectedValue.value,
          ),
      ),
    [selectedOptions],
  );
  const { t } = useTranslation('jobBoard', { keyPrefix: 'filterSection' });

  const isRoundedBorder = getIsRoundedBorder(variant);

  const selectValue = (option: O) => {
    setIsOpen(false);

    if (autocomplete) {
      if (isPreviouslyAdded(option.text)) return;
      setSearchTerm(null);
    }
    onChange(option);
  };

  const handleSearch = useCallback(
    (newValue: string) => {
      setSearchTerm(newValue);
      onSearch?.(newValue);
    },
    [onSearch],
  );

  const handleClose = useCallback(() => {
    setSearchTerm(null);
    onSearch?.('');
    setIsOpen(false);
  }, [onSearch]);

  const filteredOptions: O[] | undefined = useMemo(() => {
    if (!autocomplete || !searchTerm) return options;
    return options?.filter(option => isSubstring(option.text, searchTerm));
  }, [options, searchTerm, autocomplete]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!autocomplete) return;
    setIsOpen(true);
    handleSearch(event.target.value);
  };

  const handleKeyPress: KeyboardEventHandler = event => {
    if (event.key !== 'Enter') return;

    if (!autocomplete) {
      setIsOpen(false);
      return;
    }

    event.preventDefault();
    const existingOption = options?.find(option => isSubstring(option.text, searchTerm ?? ''));
    if (!existingOption) return;
    selectValue(existingOption);
  };

  const toggleContainer = useRef<HTMLDivElement>(null);

  useOutsideClick(toggleContainer, isOpen ? handleClose : undefined);

  const accessibleName = label ?? placeholder ?? value?.text ?? name;
  const comboboxAriaLabel = accessibleName ?? 'Dropdown';

  const triggerContent = staticTrigger ? (
    <span className='contents' inert>
      {children}
    </span>
  ) : (
    children
  );

  return (
    <div className='relative' ref={toggleContainer}>
      {label && (
        <label htmlFor={id} id={id ? `${id}-label` : undefined}>
          <p className='text-default mb-2 text-sm font-medium'>{label}</p>
        </label>
      )}
      <div
        className={mergeTailwindClasses(
          'focus-within:border-primary flex focus-within:border',
          (isRoundedBorder || variant === DropdownVariant.Primary) && 'rounded-full',
          variant === DropdownVariant.RoundedSmallerWithBorder && 'border border-solid border-gray-200 bg-white',
          variant === DropdownVariant.Primary && 'border-primary border border-solid bg-white',
          variant === DropdownVariant.Secondary && 'bg-input rounded-lg border border-solid border-gray-200',
          state === InputState.Error && 'ring-error hover:ring-error hover:focus-within:ring-error ring-2',
          disabled && 'bg-grey',
          classname,
        )}
        onClick={() => setIsOpen(!isOpen)}
        onFocus={() => onTouched?.()}
        role='combobox'
        aria-label={comboboxAriaLabel}
        aria-controls='dropdown-list'
        aria-expanded={isOpen}
        tabIndex={0}
        onKeyPress={handleKeyPress}>
        {children ? (
          triggerContent
        ) : (
          <>
            <input
              disabled={disabled}
              id={id}
              className={clsx(
                'placeholder-subtext w-full cursor-default bg-transparent pr-2 outline-hidden',
                size === 'sm' && 'text-base',
                isRoundedBorder ? 'pl-6' : 'pl-4',
                variant === DropdownVariant.Primary && 'text-primary text-lg font-medium',
                variant &&
                  [DropdownVariant.RoundedSmallerWithBorder, DropdownVariant.Secondary, DropdownVariant.Primary].includes(variant) &&
                  'py-2.5',
              )}
              placeholder={placeholder}
              value={autocomplete ? (searchTerm ?? value?.text ?? '') : value?.text}
              readOnly={!autocomplete}
              autoComplete={autocomplete ? 'off' : undefined}
              name={name}
              data-testid={name ? `dropdown-input-${name}` : undefined}
              onChange={handleInputChange}
              onBlur={() => onBlur?.()}
              ref={ref}
              {...inputProps}
            />
            <div
              className={clsx(
                'm-auto',
                isRoundedBorder ? 'mr-6' : 'mr-2',
                autocomplete ? 'flex items-center gap-2' : 'grid place-items-center',
              )}>
              {autocomplete && searchTerm && (
                <button onClick={() => handleSearch('')}>
                  <Times height={16} width={16} />
                </button>
              )}
              <Chevron className='text-default-icon rotate-90' height={20} width={20} />
            </div>
          </>
        )}
      </div>

      {isOpen && !disabled && filteredOptions && filteredOptions.length > 0 && (
        <div
          className={mergeTailwindClasses(
            'text-subtext z-popover ring-primary-200 absolute right-0 flex max-h-125 w-full min-w-39 flex-col overflow-auto rounded-2xl bg-white px-4 text-sm shadow-xl ring-1',
            isRoundedBorder ? 'mt-2.5' : 'mt-1',
            { 'max-h-64': size === 'sm' },
            dialogClassName,
          )}>
          {filteredOptions?.map(option => (
            <button
              className={clsx(
                isPreviouslyAdded(option.value) ? 'opacity-50' : 'hover:text-primary hover:font-semibold',
                value?.value === option.value ? 'text-primary font-semibold' : 'text-default',
                'flex border-b border-gray-200 px-1.5 py-4 text-left last:border-0 sm:px-3',
              )}
              key={option.value}
              data-testid={`dropdown-option-${option.text}`}
              onClick={() => selectValue(option)}
              type='button'>
              <span className='flex-1'>{option.text || '\u00A0'}</span>
              {isPreviouslyAdded(option.value) && <span className='ml-auto text-xs text-gray-500 italic'>{t('alreadySelected')}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const EMPTY_OPTION: Option = { text: '', value: '' };

export default forwardRef(DropdownComponent) as <O extends Option = Option>(
  props: DropdownProps<O> & { ref?: React.Ref<HTMLInputElement> },
) => ReactElement;
